import { rankItem, rankings } from "@tanstack/match-sorter-utils";

export type FuzzySearchOption<T> = T & {
	searchValues: string[];
};

export const fuzzyFilterOptions = <T>(
	options: FuzzySearchOption<T>[],
	inputValue?: string,
	rankOptions?: { matchToAcronyms?: boolean },
) => {
	if (!inputValue?.trim()) {
		return options.map(({ searchValues, ...option }) => option as T);
	}

	const searchTerm = inputValue.trim().toLowerCase();
	const matchToAcronyms = rankOptions?.matchToAcronyms ?? false;

	return options
		.map((option) => {
			const searchValues = option.searchValues;
			const ranks = searchValues.map((searchValue) =>
				rankItem(
					searchValue,
					inputValue.trim(),
					matchToAcronyms ? { threshold: rankings.ACRONYM } : undefined,
				),
			);
			const bestRank = ranks.find((rank) => rank.passed);

			if (!bestRank) {
				return null;
			}

			// Check different matching scenarios for stronger weighting
			let rankBonus = 0;

			for (const searchValue of searchValues) {
				const lowerSearchValue = searchValue.toLowerCase();

				// Strongest match: exact prefix match
				if (lowerSearchValue.startsWith(searchTerm)) {
					rankBonus = -1000;
					break;
				}

				// Strong match: any word starts with the search term
				const words = lowerSearchValue.split(/\s+/);
				const wordStartsWithMatch = words.some((word) => word.startsWith(searchTerm));
				if (wordStartsWithMatch && rankBonus > -500) {
					rankBonus = -500;
				}

				// Medium match: contains the search term as a contiguous substring
				if (lowerSearchValue.includes(searchTerm) && rankBonus > -300) {
					rankBonus = -300;
				}
			}

			// Apply the bonus to the rank (lower is better, so add the negative bonus)
			const adjustedRank = bestRank.rank + rankBonus;

			return { ...option, rank: { ...bestRank, rank: adjustedRank } };
		})
		.filter((option): option is NonNullable<typeof option> => option !== null)
		.sort((a, b) => a.rank.rank - b.rank.rank)
		.map(({ rank, searchValues, ...option }) => option as T);
};
